import { Link, Stack, Typography } from '@mui/material';

import { useCustomerInfo } from '@/api/customers';
import { DeviceRegistrationData } from '@/api/device/types';
import { useSubscriptionsItems } from '@/api/subscriptions';
import { useUserAccount } from '@/api/userAccount';
import { isEligibleForFreeData, showUiElementPerIridiumVar } from '@/utils';

import { GenericSkeleton } from './loaders/GenericSkeleton';

const MAX_POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 1500;

export const RegistrationResultDialogContent = ({
    registerDeviceResponse,
    hadNoPriorSubscription,
    preRegistrationBalanceCents,
}: {
    registerDeviceResponse?: DeviceRegistrationData;
    hadNoPriorSubscription: boolean | undefined;
    preRegistrationBalanceCents?: number;
}) => {
    const { data: subscriptionItems } = useSubscriptionsItems();
    const { data: accountData } = useUserAccount();
    const eligibleForFreeData = isEligibleForFreeData(registerDeviceResponse?.deviceShipDate ?? null);
    const { data: customerInfo } = useCustomerInfo({
        config: {
            refetchInterval: (query) => {
                if (!eligibleForFreeData) {
                    // If not eligible for free data, we don't expect the balance to update, so no need to poll.
                    return false;
                }
                const updateCount = query.state.dataUpdateCount;
                const currentCustomerInfo = query.state.data?.data;
                const balance = currentCustomerInfo?.balance;
                if (updateCount >= MAX_POLL_ATTEMPTS) {
                    // We've already tried polling several times, so we can stop now to avoid polling indefinitely.
                    return false;
                }
                if (preRegistrationBalanceCents !== undefined && balance !== preRegistrationBalanceCents) {
                    // Balance has updated since registration, so we can stop polling.
                    return false;
                }
                return POLL_INTERVAL_MS;
            },
        },
    });

    const customerBalance = customerInfo?.data?.balance;
    const remainingBalanceDollars = customerBalance !== undefined ? -customerBalance / 100 : undefined;
    const remainingBalanceFormatted =
        remainingBalanceDollars !== undefined
            ? `$${remainingBalanceDollars.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
            : undefined;

    const accountIridiumVar = accountData?.data?.user?.accountIridiumVar;
    /*
        TODO: multiple iridium vars support
        Account with multiple vars won't reach this text because it's only allowed to register new device
        if it matches iridium vars of all the rest of the devices in the account.
        Meaning that accounts with multiple vars won't be able to register new devices.
     */
    const showComplimentaryCreditsText = showUiElementPerIridiumVar(accountIridiumVar, 'usesSatelliteCredits', 'every');

    if (!registerDeviceResponse || !subscriptionItems?.data) {
        return <GenericSkeleton />;
    }

    const monthlySubscriptionItems = subscriptionItems.data.filter((item) => item.price_lookup_key.includes('monthly'));

    const freeInvoiceCreditBalanceAdded = registerDeviceResponse.telemetryCapabilities.reduce((acc, telemetry) => {
        const itemPrice = monthlySubscriptionItems.find((item) => item.price_lookup_key.includes(telemetry));
        if (!itemPrice) {
            return acc;
        }
        return acc + (itemPrice.unit_amount * 3) / 100;
    }, 0);

    return (
        <Stack gap={2}>
            <Typography variant="body2" gutterBottom component="li" sx={{ listStyleType: 'none' }}>
                {registerDeviceResponse.spotterId} has been successfully registered.{' '}
                {hadNoPriorSubscription && eligibleForFreeData && (
                    <>
                        {' '}
                        You have been automatically enrolled in a monthly recurring subscription and your device&apos;s
                        telemetry has been turned on.
                    </>
                )}
            </Typography>
            {eligibleForFreeData && (
                <>
                    {freeInvoiceCreditBalanceAdded > 0 && (
                        <Typography variant="body2" gutterBottom component="li" sx={{ listStyleType: 'none' }}>
                            <strong>
                                {`$${freeInvoiceCreditBalanceAdded.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                            </strong>{' '}
                            has been added to your account balance.{' '}
                            {hadNoPriorSubscription && (
                                <>
                                    This first month has already been deducted
                                    {remainingBalanceFormatted !== undefined && (
                                        <>
                                            {' '}
                                            for a remaining balance of <strong>{remainingBalanceFormatted}</strong>
                                        </>
                                    )}
                                    .{' '}
                                </>
                            )}
                            {hadNoPriorSubscription === false && (
                                <>
                                    A prorated charge for this current invoice period has already been deducted
                                    {remainingBalanceFormatted !== undefined && (
                                        <>
                                            {' '}
                                            for a remaining balance of <strong>{remainingBalanceFormatted}</strong>
                                        </>
                                    )}
                                    .{' '}
                                </>
                            )}
                            Your account balance will be automatically applied first for subscription payments before
                            charging your chosen payment method.
                        </Typography>
                    )}
                    {showComplimentaryCreditsText && (
                        <Typography
                            variant="body2"
                            gutterBottom
                            component="li"
                            sx={{ listStyleType: 'none' }}
                            data-testid="complimentary-satellite-credits-text"
                        >
                            <strong>2,200 complimentary satellite credits</strong> have also been added to your account.
                            Your devices will use these credits when transmitting over satellite.
                        </Typography>
                    )}
                </>
            )}
            <Typography variant="body2" gutterBottom component="li" sx={{ listStyleType: 'none' }}>
                View our{' '}
                <Link href={registerDeviceResponse.seatsSubscriptionLearnMoreUrl} variant="body2">
                    Subscription Renewals and Pricing documentation
                </Link>{' '}
                to learn more.
            </Typography>
        </Stack>
    );
};
